import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { LineChart, Line, BarChart, Bar, AreaChart, Area, XAxis, YAxis, CartesianGrid, ResponsiveContainer } from 'recharts';
import { getGA4ConversionsBySource } from '../../../api/auth';
import { fmtNumber, fmtCurrency } from '../../shared/format';
import {
  sortRows, downloadCsv, downloadPdf, makeExport,
  SortableHeader, PaginationFooter, TableSkeleton, SectionInsightCard, humanizeEvent, GA4DimensionNote, ViewToggle,
} from '../shared';
import ThemedChartTooltip, { ThemedTooltipBox } from '../../shared/ThemedChartTooltip';
import ThinScroll from '../../shared/ThinScroll';
import { ErrorState, EmptyState } from '../../shared/StateMessage';
import useTablePage from '../../../hooks/useTablePage';
import { expandableRowProps } from '../../shared/expandableRow';

// Event value renders currency only when positive; 0/null show an em dash
// (spec §16). Most events — page_view, scroll, etc. — carry no value, so
// "$0.00" would be noise. fmtCurrency itself stays untouched (shared app-wide).
const fmtEventValue = (value) => (value != null && Number(value) > 0 ? fmtCurrency(value) : '—');

/* ============================= Events sub-tab ============================= */
const EventsTable = ({ data, loading, error, searchQuery = '' }) => {
  const [sort, setSort] = useState({ key: 'event_count', dir: 'desc' });
  const perPage = 10;

  const handleSort = (key) => {
    setSort((prev) => prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'desc' });
  };

  const filtered = useMemo(() => {
    if (!data) return [];
    if (!searchQuery) return data;
    const query = searchQuery.toLowerCase();
    return data.filter(row => (row.event_name || '').toLowerCase().includes(query));
  }, [data, searchQuery]);

  // Must run before the early returns below — hooks can't be called conditionally.
  const { page, setPage, totalPages, sliceStart, sliceEnd } = useTablePage(filtered.length, perPage, [data, searchQuery, sort]);

  if (error) return <ErrorState message={error} />;
  if (loading) return <TableSkeleton />;
  if (!data || data.length === 0) {
    return <EmptyState message="No event data for this period." padding="py-12" />;
  }

  const sorted = sortRows(filtered, sort.key, sort.dir);
  const totalItems = sorted.length;
  const paged = sorted.slice(sliceStart, sliceEnd);

  const totals = {
    event_count: filtered.reduce((sum, row) => sum + (row.event_count || 0), 0),
    users: filtered.reduce((sum, row) => sum + (row.users || 0), 0),
    event_value: filtered.reduce((sum, row) => sum + (row.event_value || 0), 0),
  };

  return (
    <div>
      <div className="text-xs text-gray-400 dark:text-gray-500 mb-2">{totalItems} event{totalItems !== 1 ? 's' : ''}</div>
      <ThinScroll className="responsive-card-table-wrapper bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 overflow-x-auto">
        <table className="responsive-card-table w-full text-sm table-fixed">
          <thead>
            <tr className="border-b border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50">
              <SortableHeader label="Event" sortKey="event_name" currentSort={sort} onSort={handleSort} className="w-[45%]" />
              <SortableHeader label="Count" sortKey="event_count" currentSort={sort} onSort={handleSort} className="w-[18%]" />
              <SortableHeader label="Users" sortKey="users" currentSort={sort} onSort={handleSort} className="w-[18%]" />
              <SortableHeader label="Value" sortKey="event_value" currentSort={sort} onSort={handleSort} className="w-[19%]" />
            </tr>
          </thead>
          <tbody>
            {paged.map((row, index) => (
              <tr key={index} className="border-b dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition">
                <td data-label="Event" className="px-3 py-2.5 truncate">
                  <span className="font-medium text-gray-900 dark:text-gray-100">{humanizeEvent(row.event_name)}</span>
                  <span className="ml-2 text-[10px] text-gray-400 dark:text-gray-500 font-mono">{row.event_name}</span>
                </td>
                <td data-label="Count" className="px-3 py-2.5 text-gray-700 dark:text-gray-300 whitespace-nowrap">{fmtNumber(row.event_count)}</td>
                <td data-label="Users" className="px-3 py-2.5 text-gray-700 dark:text-gray-300 whitespace-nowrap">{fmtNumber(row.users)}</td>
                <td data-label="Value" className="px-3 py-2.5 text-gray-700 dark:text-gray-300 whitespace-nowrap">{fmtEventValue(row.event_value)}</td>
              </tr>
            ))}
            <tr className="bg-gray-50 dark:bg-gray-800/70 font-semibold">
              <td data-label="Total" className="px-3 py-2.5 text-gray-900 dark:text-gray-100">Total ({totalItems} events)</td>
              <td data-label="Count" className="px-3 py-2.5 text-gray-900 dark:text-gray-100 whitespace-nowrap">{fmtNumber(totals.event_count)}</td>
              <td data-label="Users" className="px-3 py-2.5 text-gray-900 dark:text-gray-100 whitespace-nowrap">{fmtNumber(totals.users)}</td>
              <td data-label="Value" className="px-3 py-2.5 text-gray-900 dark:text-gray-100 whitespace-nowrap">{fmtEventValue(totals.event_value)}</td>
            </tr>
          </tbody>
        </table>
      </ThinScroll>
      <PaginationFooter pagination={{ total_items: totalItems, total_pages: totalPages }} page={page} perPage={perPage} onPageChange={setPage} label="events" />
    </div>
  );
};

/* ============================= Conversions sub-tab (with source drill-down) ============================= */
const ConversionsTable = ({ data, loading, error, clientId, rangeStart, rangeEnd, searchQuery = '' }) => {
  const [sort, setSort] = useState({ key: 'conversions', dir: 'desc' });
  const [expandedEvent, setExpandedEvent] = useState(null);
  const [sourceData, setSourceData] = useState({});
  const [sourceLoading, setSourceLoading] = useState({});
  const [sourcePage, setSourcePage] = useState({});
  const perPage = 10;
  const sourcePerPage = 10;

  const handleSort = (key) => {
    setSort((prev) => prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'desc' });
  };

  const handleExpand = useCallback(async (eventName) => {
    if (expandedEvent === eventName) {
      setExpandedEvent(null);
      return;
    }
    setExpandedEvent(eventName);
    setSourcePage((prev) => ({ ...prev, [eventName]: 1 }));
    if (sourceData[eventName]) return;
    setSourceLoading((prev) => ({ ...prev, [eventName]: true }));
    try {
      const resp = await getGA4ConversionsBySource(clientId, rangeStart, rangeEnd, eventName);
      setSourceData((prev) => ({ ...prev, [eventName]: resp?.data ?? [] }));
    } catch {
      setSourceData((prev) => ({ ...prev, [eventName]: [] }));
    } finally {
      setSourceLoading((prev) => ({ ...prev, [eventName]: false }));
    }
  }, [expandedEvent, sourceData, clientId, rangeStart, rangeEnd]);

  useEffect(() => {
    setSourceData({});
    setExpandedEvent(null);
    setSourcePage({});
  }, [clientId, rangeStart, rangeEnd]);

  const filteredConv = useMemo(() => {
    if (!data) return [];
    if (!searchQuery) return data;
    const query = searchQuery.toLowerCase();
    return data.filter(row => (row.event_name || '').toLowerCase().includes(query));
  }, [data, searchQuery]);

  // Must run before the early returns below — hooks can't be called conditionally.
  const { page, setPage, totalPages, sliceStart, sliceEnd } = useTablePage(filteredConv.length, perPage, [data, searchQuery, sort]);

  if (error) return <ErrorState message={error} />;
  if (loading) return <TableSkeleton />;
  if (!data || data.length === 0) {
    return (
      <EmptyState message="No conversion events recorded for this period. Conversion tracking must be configured in your Google Analytics property." padding="py-12" />
    );
  }

  const sorted = sortRows(filteredConv, sort.key, sort.dir);
  const totalItems = sorted.length;
  const paged = sorted.slice(sliceStart, sliceEnd);

  const totals = {
    conversions: filteredConv.reduce((sum, row) => sum + (row.conversions || 0), 0),
    users: filteredConv.reduce((sum, row) => sum + (row.users || 0), 0),
    event_value: filteredConv.reduce((sum, row) => sum + (row.event_value || 0), 0),
  };

  return (
    <div>
      <ThinScroll className="responsive-card-table-wrapper bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 overflow-x-auto">
        <table className="responsive-card-table w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50">
              <SortableHeader label="Conversion Event" sortKey="event_name" currentSort={sort} onSort={handleSort} className="min-w-[200px]" />
              <SortableHeader label="Conversions" sortKey="conversions" currentSort={sort} onSort={handleSort} />
              <SortableHeader label="Users" sortKey="users" currentSort={sort} onSort={handleSort} />
              <SortableHeader label="Revenue" sortKey="event_value" currentSort={sort} onSort={handleSort} />
            </tr>
          </thead>
          <tbody>
            {paged.map((row) => (
              <React.Fragment key={row.event_name}>
                <tr
                  {...expandableRowProps({
                    expanded: expandedEvent === row.event_name,
                    onToggle: () => handleExpand(row.event_name),
                    label: `details for ${humanizeEvent(row.event_name)}`,
                    className: `border-b dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition cursor-pointer ${expandedEvent === row.event_name ? '' : 'rc-collapsed'}`,
                  })}
                >
                  <td data-label="Conversion Event" className={`rc-title-cell px-3 py-2.5 ${expandedEvent === row.event_name ? 'rc-title-open' : ''}`}>
                    <span className="inline-flex items-center gap-1.5 font-medium text-gray-900 dark:text-gray-100">
                      <svg className={`w-3.5 h-3.5 transition-transform ${expandedEvent === row.event_name ? 'rotate-90' : ''}`} viewBox="0 0 12 12">
                        <path d="M4 2l4 4-4 4" stroke="currentColor" strokeWidth="1.5" fill="none" />
                      </svg>
                      {humanizeEvent(row.event_name)}
                      <span className="text-[10px] text-gray-400 font-mono font-normal">{row.event_name}</span>
                    </span>
                  </td>
                  <td data-label="Conversions" className="px-3 py-2.5 text-gray-700 dark:text-gray-300">{fmtNumber(row.conversions)}</td>
                  <td data-label="Users" className="px-3 py-2.5 text-gray-700 dark:text-gray-300">{fmtNumber(row.users)}</td>
                  <td data-label="Revenue" className="px-3 py-2.5 text-gray-700 dark:text-gray-300">{fmtCurrency(row.event_value)}</td>
                </tr>
                {expandedEvent === row.event_name && (
                  sourceLoading[row.event_name] ? (
                    <tr><td colSpan={4}><TableSkeleton /></td></tr>
                  ) : (sourceData[row.event_name] && sourceData[row.event_name].length > 0) ? (() => {
                    const allSources = sourceData[row.event_name];
                    const currentSrcPage = sourcePage[row.event_name] || 1;
                    const srcTotalItems = allSources.length;
                    const srcTotalPages = Math.ceil(srcTotalItems / sourcePerPage);
                    const pagedSources = allSources.slice((currentSrcPage - 1) * sourcePerPage, currentSrcPage * sourcePerPage);
                    return (
                      <>
                        <tr className="bg-gray-50/50 dark:bg-gray-800/30">
                          <td className="px-3 py-1.5 pl-9 text-xs font-semibold text-gray-500 dark:text-gray-400" colSpan={2}>Source / Medium</td>
                          <td className="px-3 py-1.5 text-xs font-semibold text-gray-500 dark:text-gray-400">Conversions</td>
                          <td className="px-3 py-1.5 text-xs font-semibold text-gray-500 dark:text-gray-400">Revenue</td>
                        </tr>
                        {pagedSources.map((source, index) => (
                          <tr key={index} className="border-b dark:border-gray-800 bg-gray-50/50 dark:bg-gray-800/30">
                            <td className="px-3 py-2 pl-9 text-xs text-gray-700 dark:text-gray-300" colSpan={2}>
                              <span className="font-medium">{source.source || '(unknown)'}</span>
                              <span className="text-gray-400 dark:text-gray-500"> / {source.medium || '(unknown)'}</span>
                            </td>
                            <td className="px-3 py-2 text-xs text-gray-500 dark:text-gray-400">{fmtNumber(source.conversions)}</td>
                            <td className="px-3 py-2 text-xs text-gray-500 dark:text-gray-400">{fmtCurrency(source.event_value)}</td>
                          </tr>
                        ))}
                        {srcTotalPages > 1 && (
                          <tr className="bg-gray-50/50 dark:bg-gray-800/30 border-b dark:border-gray-800">
                            <td colSpan={4} className="px-3 py-2 pl-9">
                              <PaginationFooter
                                pagination={{ total_items: srcTotalItems, total_pages: srcTotalPages }}
                                page={currentSrcPage}
                                perPage={sourcePerPage}
                                onPageChange={(nextPage) => setSourcePage((prev) => ({ ...prev, [row.event_name]: nextPage }))}
                                label="sources"
                              />
                            </td>
                          </tr>
                        )}
                      </>
                    );
                  })() : (
                    <tr className="bg-gray-50/50 dark:bg-gray-800/30">
                      <td colSpan={4} className="px-3 py-3 pl-9 text-xs text-gray-400 dark:text-gray-500">No source breakdown available.</td>
                    </tr>
                  )
                )}
              </React.Fragment>
            ))}
            <tr className="bg-gray-50 dark:bg-gray-800/70 font-semibold">
              <td data-label="Total" className="px-3 py-2.5 text-gray-900 dark:text-gray-100">Total ({totalItems})</td>
              <td data-label="Conversions" className="px-3 py-2.5 text-gray-900 dark:text-gray-100">{fmtNumber(totals.conversions)}</td>
              <td data-label="Users" className="px-3 py-2.5 text-gray-900 dark:text-gray-100">{fmtNumber(totals.users)}</td>
              <td data-label="Revenue" className="px-3 py-2.5 text-gray-900 dark:text-gray-100">{fmtCurrency(totals.event_value)}</td>
            </tr>
          </tbody>
        </table>
      </ThinScroll>
      <PaginationFooter pagination={{ total_items: totalItems, total_pages: totalPages }} page={page} perPage={perPage} onPageChange={setPage} label="events" />
    </div>
  );
};

/* ============================= Conversion trend (sub-tab) ============================= */
const ConversionTrendChart = ({ trafficData, error }) => {
  const chartData = useMemo(() => {
    if (!trafficData || trafficData.length === 0) return [];
    return [...trafficData]
      .sort((rowA, rowB) => rowA.date.localeCompare(rowB.date))
      .map(row => ({
        date: new Date(row.date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        conversions: row.conversions || 0,
        sessions: row.sessions || 0,
        convRate: (row.sessions || 0) > 0 ? ((row.conversions || 0) / row.sessions) * 100 : 0,
      }));
  }, [trafficData]);

  if (error) return <ErrorState message={error} />;
  if (chartData.length === 0) {
    return <EmptyState message="No conversion trend data available." padding="py-12" />;
  }

  const totalConv = chartData.reduce((sum, datum) => sum + datum.conversions, 0);
  const totalSessions = chartData.reduce((sum, datum) => sum + datum.sessions, 0);
  const avgRate = totalSessions > 0 ? ((totalConv / totalSessions) * 100).toFixed(2) : '0.00';

  return (
    <div>
      <div className="flex items-center gap-6 mb-4">
        <div>
          <div className="text-[10px] text-gray-500 dark:text-gray-400 uppercase tracking-wide">Total Conversions</div>
          <div className="text-lg font-bold text-gray-900 dark:text-gray-100">{totalConv.toLocaleString()}</div>
        </div>
        <div>
          <div className="text-[10px] text-gray-500 dark:text-gray-400 uppercase tracking-wide">Avg. Conversion Rate</div>
          <div className="text-lg font-bold text-gray-900 dark:text-gray-100">{avgRate}%</div>
        </div>
        <div>
          <div className="text-[10px] text-gray-500 dark:text-gray-400 uppercase tracking-wide">Period</div>
          <div className="text-lg font-bold text-gray-900 dark:text-gray-100">{chartData.length} days</div>
        </div>
      </div>

      <div className="rotate-x-labels bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 mb-4">
        <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-3">Daily Conversions</h4>
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#374151" vertical={false} />
            <XAxis dataKey="date" tick={{ fontSize: 9, fill: '#9ca3af' }} axisLine={{ stroke: '#374151' }} tickLine={false} interval={Math.max(0, Math.floor(chartData.length / 8))} />
            <YAxis tick={{ fontSize: 10, fill: '#9ca3af' }} axisLine={false} tickLine={false} width={40} allowDecimals={false} />
            <ThemedChartTooltip content={({ active, payload, label }) => {
              if (!active || !payload?.length) return null;
              const datum = payload[0].payload;
              return (
                <ThemedTooltipBox title={label}>
                  <div>Conversions: {datum.conversions.toLocaleString()}</div>
                  <div>Sessions: {datum.sessions.toLocaleString()}</div>
                  <div>Conv. Rate: {datum.convRate.toFixed(2)}%</div>
                </ThemedTooltipBox>
              );
            }} />
            <Bar dataKey="conversions" fill="#32B1FF" radius={[2, 2, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="rotate-x-labels bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4">
        <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-3">Conversion Rate (%)</h4>
        <ResponsiveContainer width="100%" height={180}>
          <LineChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#374151" vertical={false} />
            <XAxis dataKey="date" tick={{ fontSize: 9, fill: '#9ca3af' }} axisLine={{ stroke: '#374151' }} tickLine={false} interval={Math.max(0, Math.floor(chartData.length / 8))} />
            <YAxis tick={{ fontSize: 10, fill: '#9ca3af' }} axisLine={false} tickLine={false} width={40} tickFormatter={value => `${value.toFixed(1)}%`} />
            <ThemedChartTooltip content={({ active, payload, label }) => {
              if (!active || !payload?.length) return null;
              return (
                <ThemedTooltipBox title={label}>
                  <div>Conv. Rate: {payload[0].value.toFixed(2)}%</div>
                </ThemedTooltipBox>
              );
            }} />
            <Line type="monotone" dataKey="convRate" stroke="#32B1FF" strokeWidth={2} dot={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

/* ============================= Conversion & revenue trend (top-level Trend View) ============================= */
const ConversionRevenueTrendChart = ({ trafficData, conversionsData, error }) => {
  const totalRevenue = useMemo(() => {
    if (!conversionsData || conversionsData.length === 0) return 0;
    return conversionsData.reduce((sum, row) => sum + (row.event_value || 0), 0);
  }, [conversionsData]);

  const chartData = useMemo(() => {
    if (!trafficData || trafficData.length === 0) return [];
    return [...trafficData]
      .sort((rowA, rowB) => rowA.date.localeCompare(rowB.date))
      .map((row) => ({
        date: row.date,
        label: new Date(row.date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        conversions: row.conversions || 0,
        sessions: row.sessions || 0,
        convRate: (row.sessions || 0) > 0 ? ((row.conversions || 0) / row.sessions) * 100 : 0,
      }));
  }, [trafficData]);

  if (error) return <ErrorState message={error} />;
  if (!trafficData || trafficData.length === 0) {
    return <EmptyState message="No conversion trend data available." padding="py-12" />;
  }

  const totalConv = chartData.reduce((sum, datum) => sum + datum.conversions, 0);
  const totalSessions = chartData.reduce((sum, datum) => sum + datum.sessions, 0);
  const avgRate = totalSessions > 0 ? ((totalConv / totalSessions) * 100).toFixed(2) : '0.00';

  return (
    <div>
      <div className="flex items-center gap-6 mb-4">
        <div>
          <div className="text-[10px] text-gray-500 dark:text-gray-400 uppercase tracking-wide">Total Conversions</div>
          <div className="text-lg font-bold text-gray-900 dark:text-gray-100">{totalConv.toLocaleString()}</div>
        </div>
        <div>
          <div className="text-[10px] text-gray-500 dark:text-gray-400 uppercase tracking-wide">Avg. Conversion Rate</div>
          <div className="text-lg font-bold text-gray-900 dark:text-gray-100">{avgRate}%</div>
        </div>
        {totalRevenue > 0 && (
          <div>
            <div className="text-[10px] text-gray-500 dark:text-gray-400 uppercase tracking-wide">Total Revenue</div>
            <div className="text-lg font-bold text-gray-900 dark:text-gray-100">${totalRevenue.toLocaleString()}</div>
          </div>
        )}
        <div>
          <div className="text-[10px] text-gray-500 dark:text-gray-400 uppercase tracking-wide">Period</div>
          <div className="text-lg font-bold text-gray-900 dark:text-gray-100">{chartData.length} days</div>
        </div>
      </div>

      <div className="rotate-x-labels bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 mb-4">
        <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-3">Daily Conversions</h4>
        <ResponsiveContainer width="100%" height={240}>
          <AreaChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
            <defs>
              <linearGradient id="convFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#32B1FF" stopOpacity={0.4} />
                <stop offset="100%" stopColor="#32B1FF" stopOpacity={0.05} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#374151" vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize: 9, fill: '#9ca3af' }} axisLine={{ stroke: '#374151' }} tickLine={false} interval={Math.max(0, Math.floor(chartData.length / 10))} />
            <YAxis tick={{ fontSize: 10, fill: '#9ca3af' }} axisLine={false} tickLine={false} width={40} allowDecimals={false} />
            <ThemedChartTooltip
              formatter={(value, name) => {
                if (name === 'conversions') return [Number(value).toLocaleString(), 'Conversions'];
                return [value, name];
              }}
            />
            <Area type="monotone" dataKey="conversions" stroke="#32B1FF" fill="url(#convFill)" strokeWidth={2} />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      <div className="rotate-x-labels bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4">
        <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-3">Conversion Rate (%)</h4>
        <ResponsiveContainer width="100%" height={180}>
          <LineChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#374151" vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize: 9, fill: '#9ca3af' }} axisLine={{ stroke: '#374151' }} tickLine={false} interval={Math.max(0, Math.floor(chartData.length / 10))} />
            <YAxis tick={{ fontSize: 10, fill: '#9ca3af' }} axisLine={false} tickLine={false} width={40} tickFormatter={(value) => `${value.toFixed(1)}%`} />
            <ThemedChartTooltip
              formatter={(value) => [`${Number(value).toFixed(2)}%`, 'Conv. Rate']}
            />
            <Line type="monotone" dataKey="convRate" stroke="#10B981" strokeWidth={2} dot={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

/* ============================= Sub-tab container ============================= */
const EventsTabContent = ({ eventsData, eventsLoading, eventsError, conversionsData, conversionsLoading, conversionsError, clientId, rangeStart, rangeEnd, searchQuery, trafficData, trafficError }) => {
  const [subTab, setSubTab] = useState('events');

  const subTabs = [
    { id: 'events', label: 'Events Breakdown' },
    { id: 'conversions', label: 'Conversion Breakdown' },
    { id: 'trend', label: 'Conversion Trend' },
  ];

  return (
    <div>
      <div className="flex items-center gap-1.5 mb-4">
        {subTabs.map((subTabItem) => (
          <button
            key={subTabItem.id}
            type="button"
            onClick={() => setSubTab(subTabItem.id)}
            className={`px-3 py-1.5 text-xs font-medium rounded-md transition ${
              subTab === subTabItem.id
                ? 'bg-[#32B1FF]/10 text-[#32B1FF] border border-[#32B1FF]/30'
                : 'bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 border border-transparent'
            }`}
          >
            {subTabItem.label}
          </button>
        ))}
      </div>

      {subTab === 'events' && (
        <EventsTable data={eventsData} loading={eventsLoading} error={eventsError} searchQuery={searchQuery} />
      )}
      {subTab === 'conversions' && (
        <>
          <div className="mb-3">
            <GA4DimensionNote />
          </div>
          <ConversionsTable
            data={conversionsData}
            loading={conversionsLoading}
            error={conversionsError}
            clientId={clientId}
            rangeStart={rangeStart}
            rangeEnd={rangeEnd}
            searchQuery={searchQuery}
          />
        </>
      )}
      {subTab === 'trend' && (
        <ConversionTrendChart trafficData={trafficData} error={trafficError} />
      )}
    </div>
  );
};

/* ============================= Insight summary ============================= */
export const getEventsSummary = (eventsData, conversionsData) => {
  if ((!eventsData || eventsData.length === 0) && (!conversionsData || conversionsData.length === 0)) return { summary: [], warnings: [] };
  const summary = [];
  if (eventsData && eventsData.length > 0) {
    const topEvent = [...eventsData].sort((rowA, rowB) => (rowB.event_count || 0) - (rowA.event_count || 0))[0];
    if (topEvent) {
      summary.push(`Most triggered event is "${topEvent.event_name}" with ${(topEvent.event_count || 0).toLocaleString()} occurrences.`);
    }
  }
  if (conversionsData && conversionsData.length > 0) {
    const topConv = [...conversionsData].sort((rowA, rowB) => (rowB.conversions || 0) - (rowA.conversions || 0))[0];
    if (topConv) {
      summary.push(`Top conversion event is "${topConv.event_name}" with ${(topConv.conversions || 0).toLocaleString()} conversions.`);
    }
  }
  return { summary, warnings: [] };
};

/* ============================= Export helpers ============================= */
// One column spec drives CSV + PDF (headers identical here). CSV emits raw values;
// PDF emits formatted count/users + a $-prefixed value (— when null).
const EVENTS_EXPORT = makeExport([
  { header: 'Event Name', get: (row) => row.event_name },
  { header: 'Count', get: (row) => row.event_count, pdf: (row) => fmtNumber(row.event_count) },
  { header: 'Users', get: (row) => row.users, pdf: (row) => fmtNumber(row.users) },
  { header: 'Value', get: (row) => row.event_value, pdf: (row) => (row.event_value != null ? `$${Number(row.event_value).toLocaleString()}` : '—') },
]);

export const exportEventsCsv = (eventsData, rangeStart, rangeEnd) => {
  if (!eventsData?.length) return;
  downloadCsv(EVENTS_EXPORT.headers, eventsData.map(EVENTS_EXPORT.toCsvRow), `ga4-events-${rangeStart}-to-${rangeEnd}.csv`);
};

export const exportEventsPdf = (eventsData, rangeStart, rangeEnd) => {
  if (!eventsData?.length) return;
  const dateRange = rangeStart && rangeEnd ? `${rangeStart} to ${rangeEnd}` : '';
  downloadPdf('Events', EVENTS_EXPORT.pdfHeaders, eventsData.map(EVENTS_EXPORT.toPdfRow), `ga4-events-${rangeStart}-to-${rangeEnd}.pdf`, dateRange);
};

/* ============================= Tab component ============================= */
const EventsTab = ({
  eventsData, eventsLoading, eventsError, conversionsData, conversionsLoading, conversionsError,
  selectedClientId, rangeStart, rangeEnd, searchQuery, trafficData, trafficError,
}) => {
  const [view, setView] = useState('table');

  return (
    <div>
      <SectionInsightCard title="Events Insight" {...getEventsSummary(eventsData, conversionsData)} />
      <ViewToggle view={view} onChange={setView} />
      {view === 'table' ? (
        <EventsTabContent
          eventsData={eventsData}
          eventsLoading={eventsLoading}
          eventsError={eventsError}
          conversionsData={conversionsData}
          conversionsLoading={conversionsLoading}
          conversionsError={conversionsError}
          clientId={selectedClientId}
          rangeStart={rangeStart}
          rangeEnd={rangeEnd}
          searchQuery={searchQuery}
          trafficData={trafficData}
          trafficError={trafficError}
        />
      ) : (
        <ConversionRevenueTrendChart trafficData={trafficData} conversionsData={conversionsData} error={trafficError} />
      )}
    </div>
  );
};

export default EventsTab;
